import { t } from "@hakili/i18n";
import { Button } from "@hakili/ui";

/**
 * `D-1020-4`, round `BANK-ROUTES`: **an address that is not hers is NAMED, never a blank page.**
 *
 * An analyste who types the chef's address, a chef who types the analyste's list, a path that names no built screen,
 * a file that is not in her portfolio: each lands here, with a sentence in French, the reason in the page's own
 * `data-motif`, and one way back. The shell (the rail) stays around it.
 *
 * **Danger sentence.** This page is courtesy, not security. The persona table decides which SCREEN is shown; what a
 * person may READ is decided by the server (6.1.8), which answers `403` or `404` to the same token whatever this
 * file renders. No data is fetched for a screen this page replaces.
 */
export type MotifPage = "reserve-chef" | "reserve-analyste" | "introuvable" | "dossier-introuvable";

const WORDS: Readonly<Record<MotifPage, readonly [Parameters<typeof t>[0], Parameters<typeof t>[0]]>> = {
  "reserve-chef": ["bank.adresse.reserveChefTitre", "bank.adresse.reserveChefTexte"],
  "reserve-analyste": ["bank.adresse.reserveAnalysteTitre", "bank.adresse.reserveAnalysteTexte"],
  introuvable: ["bank.adresse.introuvableTitre", "bank.adresse.introuvableTexte"],
  "dossier-introuvable": ["bank.adresse.dossierIntrouvableTitre", "bank.adresse.dossierIntrouvableTexte"],
};

export function PageRefusee(props: {
  readonly motif: MotifPage;
  readonly retour: string;
  readonly onRetour: () => void;
}): React.JSX.Element {
  const [title, text] = WORDS[props.motif];
  return (
    <section
      role="alert"
      className="rounded-2xl border border-gray-200 bg-white px-6 py-8"
      data-testid="bank-page-refusee"
      data-motif={props.motif}
    >
      <h1 className="text-lg font-semibold tracking-tight">{t(title)}</h1>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{t(text)}</p>
      <div className="mt-5">
        <Button variant="secondary" onClick={props.onRetour} data-testid="bank-page-refusee-retour">
          {props.retour}
        </Button>
      </div>
    </section>
  );
}
